import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Themed hover tooltip (full value); portal so it isn’t clipped by table overflow. */
export function LabDemoThemedTip({ text, children, disabled }) {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [box, setBox] = useState({ top: 0, left: 0, width: 360 });
  const showTimer = useRef(null);
  const hideTimer = useRef(null);

  const tipText = text != null ? String(text) : '';
  const canShow = !disabled && tipText.trim().length > 0;

  useEffect(() => {
    if (!disabled) return;
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
    setVisible(false);
  }, [disabled]);

  const position = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(440, window.innerWidth - 20);
    let left = r.left + r.width / 2 - w / 2;
    left = Math.max(10, Math.min(left, window.innerWidth - w - 10));
    let top = r.bottom + 8;
    const estH = 120;
    if (top + estH > window.innerHeight - 12) {
      top = Math.max(10, r.top - estH - 8);
    }
    setBox({ top, left, width: w });
  }, []);

  const show = () => {
    if (!canShow) return;
    clearTimeout(hideTimer.current);
    showTimer.current = window.setTimeout(() => {
      position();
      setVisible(true);
    }, 280);
  };

  const hide = () => {
    clearTimeout(showTimer.current);
    hideTimer.current = window.setTimeout(() => setVisible(false), 120);
  };

  const cancelHide = () => {
    clearTimeout(hideTimer.current);
  };

  useLayoutEffect(() => {
    if (!visible) return;
    position();
    const onScroll = () => position();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [visible, position]);

  return (
    <>
      <span ref={anchorRef} className="lab-demo-tip-wrap" onMouseEnter={show} onMouseLeave={hide}>
        {children}
      </span>
      {visible &&
        canShow &&
        createPortal(
          <div
            className="lab-demo-themed-tooltip"
            style={{ top: box.top, left: box.left, width: box.width }}
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
            role="tooltip"
          >
            <div className="lab-demo-themed-tooltip__inner">{tipText}</div>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Focus opens a larger editor in a small anchored panel (not a full-screen modal).
 * Typing happens in the overlay; the inline control stays for layout and shows the same value.
 */
export function LabDemoExpandText({
  multiline,
  value,
  onChange,
  className = '',
  style = {},
  placeholder,
  disabled,
  inputClassName = '',
  onOpenChange
}) {
  const hostRef = useRef(null);
  const overlayWrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ top: 0, left: 0, width: 520 });

  const place = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    const w = Math.min(560, window.innerWidth - 24);
    let left = r.left + r.width / 2 - w / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - w - 12));
    const estH = multiline ? 200 : 56;
    let top = r.bottom + 6;
    if (top + estH > window.innerHeight - 12) {
      top = Math.max(12, r.top - estH - 8);
    }
    setBox({ top, left, width: w });
  }, [multiline]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const root = overlayWrapRef.current;
      if (!root) return;
      const el = multiline ? root.querySelector('textarea') : root.querySelector('input');
      el?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, multiline]);

  const close = () => setOpen(false);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const onHostFocus = () => {
    if (disabled) return;
    setOpen(true);
  };

  const mergedStyle = open ? { ...style, opacity: 0.35 } : style;

  return (
    <>
      <div ref={hostRef} className="lab-demo-expand-host">
        {multiline ? (
          <textarea
            className={`${className} ${inputClassName}`.trim()}
            style={mergedStyle}
            value={value}
            onChange={onChange}
            onFocus={onHostFocus}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={open}
            rows={multiline ? 2 : undefined}
          />
        ) : (
          <input
            type="text"
            className={`${className} ${inputClassName}`.trim()}
            style={mergedStyle}
            value={value}
            onChange={onChange}
            onFocus={onHostFocus}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={open}
          />
        )}
      </div>
      {open &&
        createPortal(
          <>
            <button
              type="button"
              className="lab-demo-field-backdrop"
              aria-label="Close editor"
              onClick={close}
            />
            <div
              ref={overlayWrapRef}
              className="lab-demo-field-overlay"
              style={{ top: box.top, left: box.left, width: box.width }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="lab-demo-field-overlay__label">Edit (Esc or outside to close)</div>
              {multiline ? (
                <textarea
                  className="form-textarea lab-demo-field-overlay__control"
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  rows={10}
                  placeholder={placeholder}
                />
              ) : (
                <input
                  type="text"
                  className="form-input lab-demo-field-overlay__control"
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  placeholder={placeholder}
                />
              )}
              <div className="lab-demo-field-overlay__actions">
                <button type="button" className="btn btn-primary" onClick={close}>
                  Done
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

/** Tooltip + expand field: hover text is hidden while the edit overlay is open. */
export function LabDemoTippedExpand({
  tipText,
  multiline,
  value,
  onChange,
  className = '',
  style = {},
  placeholder,
  disabled,
  inputClassName = ''
}) {
  const [editing, setEditing] = useState(false);
  return (
    <LabDemoThemedTip text={tipText} disabled={editing}>
      <LabDemoExpandText
        multiline={multiline}
        value={value}
        onChange={onChange}
        className={className}
        style={style}
        placeholder={placeholder}
        disabled={disabled}
        inputClassName={inputClassName}
        onOpenChange={setEditing}
      />
    </LabDemoThemedTip>
  );
}
